// =============================================================================
// SERVICES/GRAPHSERVICE.TS - Chamadas à Microsoft Graph API (SPEC 1)
// =============================================================================
// Padrão idêntico ao central-reunioes/src/services/graphService.js.
// Escopo necessário para listarUsuariosTenant: User.ReadBasic.All (delegado).
// =============================================================================

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

async function graphFetch<T = unknown>(accessToken: string, endpoint: string): Promise<T> {
  const url = `${GRAPH_BASE}${endpoint}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(err?.error?.message ?? `Graph API ${response.status}: ${endpoint}`);
  }
  if (response.status === 204) return null as T;
  return response.json() as Promise<T>;
}

export interface UsuarioTenant {
  id: string;
  displayName: string;
  email: string;
  accountEnabled: boolean;
}

interface GraphUsersResponse {
  value: Array<{
    id: string;
    displayName?: string;
    mail?: string;
    userPrincipalName?: string;
    accountEnabled?: boolean;
    assignedLicenses?: unknown[];
  }>;
  '@odata.nextLink'?: string;
}

/**
 * listarUsuariosTenant - lista contas com licença ativa do tenant M365.
 * Mesmo padrão do central-reunioes/src/services/graphService.js.
 * Escopo: User.ReadBasic.All (delegado, já consentido no tenant EJFA).
 */
export async function listarUsuariosTenant(accessToken: string): Promise<UsuarioTenant[]> {
  const usuarios: UsuarioTenant[] = [];
  let endpoint: string | null =
    '/users?$select=id,displayName,mail,userPrincipalName,accountEnabled,assignedLicenses&$top=999';

  for (let i = 0; i < 20 && endpoint; i++) {
    const page: GraphUsersResponse | null = await graphFetch<GraphUsersResponse>(accessToken, endpoint);
    if (!page) break;
    for (const u of page.value ?? []) {
      const email = (u.mail ?? u.userPrincipalName ?? '').toLowerCase();
      if (!email) continue;
      if (!Array.isArray(u.assignedLicenses) || u.assignedLicenses.length === 0) continue;
      usuarios.push({
        id: u.id,
        displayName: u.displayName ?? email,
        email,
        accountEnabled: u.accountEnabled !== false,
      });
    }
    const nextLink: string | undefined = page['@odata.nextLink'];
    endpoint = nextLink ? nextLink.replace('https://graph.microsoft.com/v1.0', '') : null;
  }

  return usuarios.sort((a, b) => a.displayName.localeCompare(b.displayName, 'pt-BR'));
}
